import { useState, useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Card,
  Typography,
  Button,
  Chip,
  Grid,
  IconButton,
  CircularProgress,
  alpha,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Badge,
  TextField,
  InputAdornment,
  Paper,
  Collapse,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import PersonIcon from '@mui/icons-material/Person';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SearchIcon from '@mui/icons-material/Search';
import RouteIcon from '@mui/icons-material/Route';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import MapIcon from '@mui/icons-material/Map';
import ReplayIcon from '@mui/icons-material/Replay';
import TrafficIcon from '@mui/icons-material/Traffic';
import SaveIcon from '@mui/icons-material/Save';
import axiosInstance from '../../lib/axios';
import { ColorModeContext } from '../../contexts/ColorModeContext';
import { palette, typography } from '../../theme/colors';
import { useAnimationStyles } from '../../hooks/useAnimations';
import { Delivery, DeliveryStatus, User, Location } from '../../types';
import { GoogleMapsWrapper, MapMarker } from '../../components/GoogleMapsWrapper';
import { DriverAssignmentModal } from '../../components/DriverAssignmentModal';

interface DeliveryWithDistance extends Delivery {
  estimatedDistance?: number;
  estimatedDuration?: number;
}

interface DriverWithDeliveries extends User {
  assignedDeliveries: Delivery[];
  totalDistance?: number;
  totalDuration?: number;
}

const fetchPendingDeliveries = async (): Promise<DeliveryWithDistance[]> => {
  const response = await axiosInstance.get('/deliveries', {
    params: {
      status: 'pending,assigned',
      limit: 100,
    },
  });

  const data = Array.isArray(response.data) ? response.data : response.data.data || [];
  return data;
};

// Fetch delivery IDs that are already part of saved (non-cancelled) routes
const fetchRoutedDeliveryIds = async (): Promise<string[]> => {
  try {
    const response = await axiosInstance.get('/delivery-routes/routed-delivery-ids');
    return response.data?.deliveryIds || [];
  } catch {
    return [];
  }
};

const fetchDrivers = async (): Promise<User[]> => {
  // Use dedicated /users/drivers endpoint which filters by userType=platform_staff AND roleSystemKey=driver
  const response = await axiosInstance.get('/users/drivers', {
    params: {
      limit: 100,
    },
  });

  return Array.isArray(response.data) ? response.data : response.data.data || [];
};

const fetchPickupLocation = async (): Promise<Location | null> => {
  try {
    const response = await axiosInstance.get('/locations/current');
    return response.data;
  } catch {
    return null;
  }
};

// Generate deterministic lat/lng offset based on string hash (for demo without geocoding)
const hashString = (str: string): number => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
};

const getDeliveryPosition = (delivery: Delivery): { lat: number; lng: number } => {
  // Base coordinates for Algiers
  const baseLat = 36.7538;
  const baseLng = 3.0588;

  // Use delivery ID + address to generate deterministic offset
  const addressStr = `${delivery.id}-${delivery.deliveryAddress?.street || ''}-${delivery.deliveryAddress?.city || ''}`;
  const hash = hashString(addressStr);

  // Generate offset between -0.08 and 0.08 (~8km radius)
  const latOffset = ((hash % 1000) / 1000 - 0.5) * 0.16;
  const lngOffset = (((hash >> 10) % 1000) / 1000 - 0.5) * 0.16;

  return {
    lat: baseLat + latOffset,
    lng: baseLng + lngOffset,
  };
};

// Helper to load Google Maps API script
const loadGoogleMapsScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    // Check if already loaded
    if (window.google?.maps) {
      resolve();
      return;
    }

    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!apiKey) {
      reject(new Error('Google Maps API key not configured'));
      return;
    }

    // Check if script is already in document
    const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
    if (existingScript) {
      // Wait for it to load
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', () => reject(new Error('Failed to load Google Maps')));
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=routes`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Google Maps'));
    document.head.appendChild(script);
  });
};

interface OptimizeRouteOptions {
  returnToOrigin?: boolean;
  trafficAware?: boolean;
  departureTime?: Date;
}

interface OptimizeRouteResult {
  optimizedOrder: string[];
  totalDistance: number;
  totalDuration: number;
  returnToOrigin: boolean;
  trafficAware: boolean;
}

const optimizeRoute = async (
  deliveries: Delivery[],
  pickupLocation: Location | null,
  options: OptimizeRouteOptions = {}
): Promise<OptimizeRouteResult> => {
  const { returnToOrigin = false, trafficAware = false, departureTime } = options;

  // Get pickup location coordinates
  const origin = pickupLocation?.latitude && pickupLocation?.longitude
    ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
    : { lat: 36.7538, lng: 3.0588 }; // Default Algiers

  // Get delivery positions
  const deliveryPositions = deliveries.map((d) => ({
    id: d.id,
    position: getDeliveryPosition(d),
  }));

  if (deliveryPositions.length < 2) {
    // For single delivery, no optimization needed
    return {
      optimizedOrder: deliveries.map((d) => d.id),
      totalDistance: 5,
      totalDuration: 15,
      returnToOrigin,
      trafficAware,
    };
  }

  try {
    // Ensure Google Maps is loaded
    await loadGoogleMapsScript();

    const directionsService = new google.maps.DirectionsService();

    let waypoints: google.maps.DirectionsWaypoint[];
    let destination: google.maps.LatLng;

    if (returnToOrigin) {
      // All deliveries are waypoints, destination is back to origin
      waypoints = deliveryPositions.map((dp) => ({
        location: new google.maps.LatLng(dp.position.lat, dp.position.lng),
        stopover: true,
      }));
      destination = new google.maps.LatLng(origin.lat, origin.lng);
    } else {
      // Last delivery is the destination
      waypoints = deliveryPositions.slice(0, -1).map((dp) => ({
        location: new google.maps.LatLng(dp.position.lat, dp.position.lng),
        stopover: true,
      }));
      const lastDelivery = deliveryPositions[deliveryPositions.length - 1];
      destination = new google.maps.LatLng(
        lastDelivery.position.lat,
        lastDelivery.position.lng
      );
    }

    // Build request options
    const requestOptions: google.maps.DirectionsRequest = {
      origin: new google.maps.LatLng(origin.lat, origin.lng),
      destination,
      waypoints,
      optimizeWaypoints: true,
      travelMode: google.maps.TravelMode.DRIVING,
    };

    // Add departure time for traffic-aware routing
    if (trafficAware && departureTime) {
      requestOptions.drivingOptions = {
        departureTime: departureTime,
        trafficModel: google.maps.TrafficModel.BEST_GUESS,
      };
    }

    const response = await directionsService.route(requestOptions);

    if (!response.routes || response.routes.length === 0) {
      throw new Error('No routes found');
    }

    const route = response.routes[0];
    const waypointOrder = route.waypoint_order || [];

    // Calculate total distance and duration from all legs
    let totalDistance = 0; // in meters
    let totalDuration = 0; // in seconds

    route.legs.forEach((leg) => {
      totalDistance += leg.distance?.value || 0;
      // Use duration_in_traffic if available (for traffic-aware routing)
      totalDuration += (leg as any).duration_in_traffic?.value || leg.duration?.value || 0;
    });

    // Build the optimized order of delivery IDs
    let optimizedOrder: string[];

    if (returnToOrigin) {
      // All deliveries were waypoints
      optimizedOrder = waypointOrder.map(
        (originalIndex: number) => deliveryPositions[originalIndex].id
      );
    } else {
      // Waypoints are deliveries 0 to n-2, last delivery is destination
      const waypointDeliveryIds = waypointOrder.map(
        (originalIndex: number) => deliveryPositions[originalIndex].id
      );
      const lastDelivery = deliveryPositions[deliveryPositions.length - 1];
      optimizedOrder = [...waypointDeliveryIds, lastDelivery.id];
    }

    return {
      optimizedOrder,
      totalDistance: Math.round(totalDistance / 1000), // Convert to km
      totalDuration: Math.round(totalDuration / 60), // Convert to minutes
      returnToOrigin,
      trafficAware,
    };
  } catch (error) {
    console.error('Route optimization failed:', error);
    // Fallback to original order
    return {
      optimizedOrder: deliveries.map((d) => d.id),
      totalDistance: deliveries.length * 5,
      totalDuration: deliveries.length * 15,
      returnToOrigin,
      trafficAware,
    };
  }
};

// API function to save route to database
const saveRouteToDatabase = async (data: {
  name?: string;
  originLocationId: string;
  deliveryIds: string[];
  returnToOrigin: boolean;
  trafficAware: boolean;
  departureTime?: string;
  driverId?: string;
}) => {
  const response = await axiosInstance.post('/delivery-routes', data);
  return response.data;
};

export const DeliveryScheduleTab = () => {
  const { mode } = useContext(ColorModeContext);
  useAnimationStyles(); // Initialize animations
  const queryClient = useQueryClient();

  const [searchValue, setSearchValue] = useState('');
  const [selectedDeliveries, setSelectedDeliveries] = useState<string[]>([]);
  const [expandedDrivers, setExpandedDrivers] = useState<string[]>([]);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedDeliveryForAssign, setSelectedDeliveryForAssign] =
    useState<Delivery | null>(null);
  const [optimizing, setOptimizing] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState<string[] | null>(null);
  const [routeStats, setRouteStats] = useState<{ distance: number; duration: number } | null>(null);

  // New state for route options
  const [returnToOrigin, setReturnToOrigin] = useState(false);
  const [trafficAware, setTrafficAware] = useState(false);
  const [departureTime, setDepartureTime] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const { data: allDeliveries = [], isLoading: loadingDeliveries } = useQuery({
    queryKey: ['deliveries', 'pending'],
    queryFn: fetchPendingDeliveries,
  });

  const { data: routedDeliveryIds = [] } = useQuery({
    queryKey: ['routed-delivery-ids'],
    queryFn: fetchRoutedDeliveryIds,
  });

  // Filter out deliveries that are already in saved routes
  const deliveries = useMemo(() => {
    const routedSet = new Set(routedDeliveryIds);
    return allDeliveries.filter((d) => !routedSet.has(d.id));
  }, [allDeliveries, routedDeliveryIds]);

  const { data: drivers = [], isLoading: loadingDrivers } = useQuery({
    queryKey: ['drivers'],
    queryFn: fetchDrivers,
  });

  const { data: pickupLocation } = useQuery({
    queryKey: ['pickupLocation'],
    queryFn: fetchPickupLocation,
  });

  // Group deliveries by driver
  const driversWithDeliveries: DriverWithDeliveries[] = useMemo(() => {
    return drivers.map((driver) => ({
      ...driver,
      assignedDeliveries: deliveries.filter((d) => d.driverId === driver.id),
    }));
  }, [drivers, deliveries]);

  // Unassigned deliveries
  const unassignedDeliveries = useMemo(() => {
    return deliveries.filter((d) => !d.driverId && d.status === 'pending');
  }, [deliveries]);

  // Filter deliveries by search
  const filteredUnassigned = useMemo(() => {
    if (!searchValue) return unassignedDeliveries;
    const search = searchValue.toLowerCase();
    return unassignedDeliveries.filter(
      (d) =>
        d.customerName?.toLowerCase().includes(search) ||
        d.deliveryAddress?.street?.toLowerCase().includes(search) ||
        d.deliveryAddress?.city?.toLowerCase().includes(search)
    );
  }, [unassignedDeliveries, searchValue]);

  // Deliveries to display in the list (reordered when optimized)
  const displayDeliveries = useMemo(() => {
    if (!optimizedRoute || optimizedRoute.length === 0) {
      return filteredUnassigned;
    }
    // Reorder to match optimized route
    const deliveryMap = new Map(filteredUnassigned.map((d) => [d.id, d]));
    const orderedDeliveries = optimizedRoute
      .map((id) => deliveryMap.get(id))
      .filter((d): d is Delivery => d !== undefined);
    // Add any deliveries not in optimizedRoute (shouldn't happen, but safety)
    const orderedIds = new Set(optimizedRoute);
    const remaining = filteredUnassigned.filter((d) => !orderedIds.has(d.id));
    return [...orderedDeliveries, ...remaining];
  }, [filteredUnassigned, optimizedRoute]);

  // Build map markers
  const mapMarkers: MapMarker[] = useMemo(() => {
    const markers: MapMarker[] = [];

    // Pickup location marker - use coordinates if available, otherwise default Algiers
    const storePosition = pickupLocation?.latitude && pickupLocation?.longitude
      ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
      : { lat: 36.7538, lng: 3.0588 }; // Default Algiers center

    markers.push({
      id: 'pickup',
      position: storePosition,
      title: pickupLocation?.name || 'Store',
      icon: 'store',
      info: (
        <Box>
          <Typography variant="subtitle2">{pickupLocation?.name || 'Store'}</Typography>
          <Typography variant="caption" color="text.secondary">
            {returnToOrigin && optimizedRoute ? 'Start & End Point' : 'Pickup Location'}
          </Typography>
        </Box>
      ),
    });

    // Delivery markers - only for selected or all unassigned
    let deliveriesToShow =
      selectedDeliveries.length > 0
        ? deliveries.filter((d) => selectedDeliveries.includes(d.id))
        : unassignedDeliveries;

    // If we have an optimized route, reorder deliveries to match
    if (optimizedRoute && optimizedRoute.length > 0) {
      const deliveryMap = new Map(deliveriesToShow.map((d) => [d.id, d]));
      deliveriesToShow = optimizedRoute
        .map((id) => deliveryMap.get(id))
        .filter((d): d is Delivery => d !== undefined);
    }

    deliveriesToShow.forEach((delivery, index) => {
      // Use deterministic position based on delivery ID and address
      const position = getDeliveryPosition(delivery);

      markers.push({
        id: delivery.id,
        position,
        title: delivery.customerName || `Delivery ${index + 1}`,
        // When optimized, the order in the array IS the optimized order (1-indexed)
        label: optimizedRoute ? String(index + 1) : undefined,
        icon: delivery.driverId ? 'driver' : 'delivery',
        info: (
          <Box>
            <Typography variant="subtitle2">{delivery.customerName}</Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {delivery.deliveryAddress?.street}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {delivery.deliveryAddress?.city}
            </Typography>
          </Box>
        ),
      });
    });

    return markers;
  }, [
    pickupLocation,
    deliveries,
    unassignedDeliveries,
    selectedDeliveries,
    optimizedRoute,
    returnToOrigin,
  ]);

  const handleToggleDelivery = (deliveryId: string) => {
    setSelectedDeliveries((prev) =>
      prev.includes(deliveryId)
        ? prev.filter((id) => id !== deliveryId)
        : [...prev, deliveryId]
    );
    setOptimizedRoute(null);
    setRouteStats(null);
  };

  const handleSelectAll = () => {
    if (selectedDeliveries.length === filteredUnassigned.length) {
      setSelectedDeliveries([]);
    } else {
      setSelectedDeliveries(filteredUnassigned.map((d) => d.id));
    }
    setOptimizedRoute(null);
    setRouteStats(null);
  };

  const handleToggleDriver = (driverId: string) => {
    setExpandedDrivers((prev) =>
      prev.includes(driverId)
        ? prev.filter((id) => id !== driverId)
        : [...prev, driverId]
    );
  };

  const handleOptimizeRoute = async () => {
    if (selectedDeliveries.length < 2) return;

    setOptimizing(true);
    try {
      const selectedDeliveriesData = deliveries.filter((d) =>
        selectedDeliveries.includes(d.id)
      );
      const result = await optimizeRoute(selectedDeliveriesData, pickupLocation ?? null, {
        returnToOrigin,
        trafficAware,
        departureTime: trafficAware && departureTime ? new Date(departureTime) : undefined,
      });
      setOptimizedRoute(result.optimizedOrder);
      setRouteStats({ distance: result.totalDistance, duration: result.totalDuration });
    } catch (error) {
      console.error('Route optimization failed:', error);
    } finally {
      setOptimizing(false);
    }
  };

  const handleSaveRoute = async () => {
    if (!optimizedRoute || optimizedRoute.length === 0 || !pickupLocation) return;

    setSaving(true);
    try {
      await saveRouteToDatabase({
        name: `Route ${new Date().toLocaleDateString()} - ${selectedDeliveries.length} deliveries`,
        originLocationId: pickupLocation.id,
        deliveryIds: optimizedRoute,
        returnToOrigin,
        trafficAware,
        departureTime: trafficAware && departureTime ? new Date(departureTime).toISOString() : undefined,
      });

      // Clear selection and optimized route after saving
      setSelectedDeliveries([]);
      setOptimizedRoute(null);
      setRouteStats(null);

      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['routed-delivery-ids'] });
      queryClient.invalidateQueries({ queryKey: ['delivery-routes'] });

      setSnackbar({
        open: true,
        message: 'Route saved successfully!',
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to save route:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save route. Please try again.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignSelected = () => {
    if (selectedDeliveries.length === 0) return;
    // For now, open modal with first selected delivery
    const firstDelivery = deliveries.find((d) => d.id === selectedDeliveries[0]);
    if (firstDelivery) {
      setSelectedDeliveryForAssign(firstDelivery);
      setAssignModalOpen(true);
    }
  };

  const handleOpenAssignModal = (delivery: Delivery) => {
    setSelectedDeliveryForAssign(delivery);
    setAssignModalOpen(true);
  };

  const getStatusColor = (status: DeliveryStatus) => {
    const colors: Record<DeliveryStatus, string> = {
      pending: palette.warning,
      assigned: palette.info,
      picked_up: palette.primary,
      out_for_delivery: palette.accent,
      delivered: palette.success,
      failed: palette.error,
      cancelled: palette.textMuted,
    };
    return colors[status];
  };

  const isLoading = loadingDeliveries || loadingDrivers;

  return (
    <Box>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left Panel - Unassigned Deliveries */}
          <Grid item xs={12} md={4}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                height: 'calc(100vh - 220px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box sx={{ p: 2, borderBottom: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}` }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: typography.fontWeight.semibold }}
                  >
                    Unassigned Deliveries
                  </Typography>
                  <Badge badgeContent={filteredUnassigned.length} color="warning">
                    <LocalShippingIcon />
                  </Badge>
                </Box>

                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search deliveries..."
                  value={searchValue}
                  onChange={(e) => setSearchValue(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: palette.textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                />

                {filteredUnassigned.length > 0 && (
                  <>
                    <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={handleSelectAll}
                      >
                        {selectedDeliveries.length === filteredUnassigned.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </Button>
                      {selectedDeliveries.length > 1 && (
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={optimizing ? <CircularProgress size={14} /> : <RouteIcon />}
                          onClick={handleOptimizeRoute}
                          disabled={optimizing}
                        >
                          Optimize
                        </Button>
                      )}
                      {optimizedRoute && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={saving ? <CircularProgress size={14} /> : <SaveIcon />}
                          onClick={handleSaveRoute}
                          disabled={saving}
                        >
                          Save Route
                        </Button>
                      )}
                      {selectedDeliveries.length > 0 && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<DirectionsCarIcon />}
                          onClick={handleAssignSelected}
                          sx={{
                            bgcolor: palette.primary,
                            '&:hover': { bgcolor: alpha(palette.primary, 0.9) },
                          }}
                        >
                          Assign
                        </Button>
                      )}
                    </Box>

                    {/* Route optimization options */}
                    {selectedDeliveries.length > 1 && (
                      <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: alpha(palette.primary, 0.03), border: `1px solid ${alpha(palette.primary, 0.1)}` }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, mb: 1, display: 'block' }}>
                          Optimization Options
                        </Typography>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={returnToOrigin}
                                onChange={(e) => {
                                  setReturnToOrigin(e.target.checked);
                                  setOptimizedRoute(null);
                                  setRouteStats(null);
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <ReplayIcon sx={{ fontSize: 16, color: palette.textMuted }} />
                                <Typography variant="caption">Return to store</Typography>
                              </Box>
                            }
                          />
                          <FormControlLabel
                            control={
                              <Switch
                                size="small"
                                checked={trafficAware}
                                onChange={(e) => {
                                  setTrafficAware(e.target.checked);
                                  setOptimizedRoute(null);
                                  setRouteStats(null);
                                }}
                              />
                            }
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <TrafficIcon sx={{ fontSize: 16, color: palette.textMuted }} />
                                <Typography variant="caption">Traffic-aware</Typography>
                              </Box>
                            }
                          />
                          {trafficAware && (
                            <TextField
                              size="small"
                              type="datetime-local"
                              label="Departure time"
                              value={departureTime}
                              onChange={(e) => {
                                setDepartureTime(e.target.value);
                                setOptimizedRoute(null);
                                setRouteStats(null);
                              }}
                              InputLabelProps={{ shrink: true }}
                              sx={{ mt: 1 }}
                            />
                          )}
                        </Box>
                      </Box>
                    )}
                  </>
                )}
              </Box>

              <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                {displayDeliveries.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <CheckCircleIcon sx={{ fontSize: 48, color: palette.success, mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      All deliveries assigned!
                    </Typography>
                  </Box>
                ) : (
                  displayDeliveries.map((delivery, index) => (
                    <ListItem
                      key={delivery.id}
                      sx={{
                        borderRadius: 2,
                        mb: 1,
                        border: `1px solid ${
                          selectedDeliveries.includes(delivery.id)
                            ? palette.primary
                            : mode === 'dark'
                              ? alpha('#fff', 0.1)
                              : alpha('#000', 0.1)
                        }`,
                        bgcolor: selectedDeliveries.includes(delivery.id)
                          ? alpha(palette.primary, 0.05)
                          : 'transparent',
                        cursor: 'pointer',
                        '&:hover': {
                          bgcolor: alpha(palette.primary, 0.05),
                        },
                      }}
                      onClick={() => handleToggleDelivery(delivery.id)}
                    >
                      <ListItemAvatar>
                        <Avatar
                          sx={{
                            bgcolor: optimizedRoute
                              ? alpha(palette.success, 0.1)
                              : alpha(palette.warning, 0.1),
                            color: optimizedRoute ? palette.success : palette.warning,
                            width: 36,
                            height: 36,
                          }}
                        >
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Typography variant="subtitle2" noWrap>
                            {delivery.customerName || 'Unknown Customer'}
                          </Typography>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                            >
                              <LocationOnIcon sx={{ fontSize: 12 }} />
                              {delivery.deliveryAddress?.street || 'No address'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {delivery.deliveryAddress?.city}
                            </Typography>
                          </Box>
                        }
                      />
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenAssignModal(delivery);
                        }}
                        sx={{ ml: 1 }}
                      >
                        <DirectionsCarIcon fontSize="small" />
                      </IconButton>
                    </ListItem>
                  ))
                )}
              </List>
            </Card>
          </Grid>

          {/* Center Panel - Map */}
          <Grid item xs={12} md={5}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                height: 'calc(100vh - 220px)',
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <MapIcon sx={{ color: palette.primary }} />
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: typography.fontWeight.semibold }}
                  >
                    Delivery Map
                  </Typography>
                </Box>
                {optimizedRoute && (
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    {routeStats && (
                      <>
                        <Chip
                          size="small"
                          label={`${routeStats.distance} km`}
                          icon={<RouteIcon />}
                          sx={{
                            bgcolor: alpha(palette.info, 0.1),
                            color: palette.info,
                          }}
                        />
                        <Chip
                          size="small"
                          label={`${routeStats.duration} min`}
                          icon={<ScheduleIcon />}
                          sx={{
                            bgcolor: alpha(palette.warning, 0.1),
                            color: palette.warning,
                          }}
                        />
                      </>
                    )}
                    {returnToOrigin && (
                      <Chip
                        size="small"
                        label="Round Trip"
                        icon={<ReplayIcon />}
                        sx={{
                          bgcolor: alpha(palette.accent, 0.1),
                          color: palette.accent,
                        }}
                      />
                    )}
                    {trafficAware && (
                      <Chip
                        size="small"
                        label="Traffic"
                        icon={<TrafficIcon />}
                        sx={{
                          bgcolor: alpha(palette.primary, 0.1),
                          color: palette.primary,
                        }}
                      />
                    )}
                    <Chip
                      size="small"
                      label="Optimized"
                      icon={<CheckCircleIcon />}
                      sx={{
                        bgcolor: alpha(palette.success, 0.1),
                        color: palette.success,
                      }}
                    />
                  </Box>
                )}
              </Box>
              <Box sx={{ height: 'calc(100% - 56px)' }}>
                <GoogleMapsWrapper
                  markers={mapMarkers}
                  height="100%"
                  darkMode={mode === 'dark'}
                  showDirections={!!optimizedRoute && selectedDeliveries.length > 1}
                  origin={
                    pickupLocation?.latitude && pickupLocation?.longitude
                      ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
                      : undefined
                  }
                  destination={
                    returnToOrigin
                      ? // Round trip: return to origin
                        pickupLocation?.latitude && pickupLocation?.longitude
                          ? { lat: pickupLocation.latitude, lng: pickupLocation.longitude }
                          : undefined
                      : // One-way: last delivery marker (mapMarkers[0] is the store)
                        optimizedRoute && mapMarkers.length > 1
                          ? mapMarkers[mapMarkers.length - 1].position
                          : undefined
                  }
                  waypoints={
                    returnToOrigin
                      ? // Round trip: all deliveries are waypoints
                        optimizedRoute && mapMarkers.length > 1
                          ? mapMarkers.slice(1).map((m) => m.position)
                          : []
                      : // One-way: skip index 0 (store) and last (destination)
                        optimizedRoute && mapMarkers.length > 2
                          ? mapMarkers.slice(1, -1).map((m) => m.position)
                          : []
                  }
                />
              </Box>
            </Card>
          </Grid>

          {/* Right Panel - Drivers */}
          <Grid item xs={12} md={3}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                height: 'calc(100vh - 220px)',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderBottom: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: typography.fontWeight.semibold }}
                  >
                    Drivers
                  </Typography>
                  <Badge badgeContent={drivers.length} color="primary">
                    <PersonIcon />
                  </Badge>
                </Box>
              </Box>

              <List sx={{ flex: 1, overflow: 'auto', p: 1 }}>
                {driversWithDeliveries.length === 0 ? (
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <PersonIcon sx={{ fontSize: 48, color: palette.textMuted, mb: 1 }} />
                    <Typography variant="body1" color="text.secondary">
                      No drivers available
                    </Typography>
                  </Box>
                ) : (
                  driversWithDeliveries.map((driver) => (
                    <Paper
                      key={driver.id}
                      elevation={0}
                      sx={{
                        mb: 1,
                        borderRadius: 2,
                        border: `1px solid ${mode === 'dark' ? alpha('#fff', 0.1) : alpha('#000', 0.1)}`,
                        overflow: 'hidden',
                      }}
                    >
                      <ListItem
                        sx={{
                          cursor: 'pointer',
                          '&:hover': { bgcolor: alpha(palette.primary, 0.03) },
                        }}
                        onClick={() => handleToggleDriver(driver.id)}
                      >
                        <ListItemAvatar>
                          <Badge
                            badgeContent={driver.assignedDeliveries.length}
                            color="primary"
                            overlap="circular"
                          >
                            <Avatar
                              sx={{
                                bgcolor: alpha(palette.primary, 0.1),
                                color: palette.primary,
                                width: 36,
                                height: 36,
                              }}
                            >
                              {driver.firstName?.[0]}
                              {driver.lastName?.[0]}
                            </Avatar>
                          </Badge>
                        </ListItemAvatar>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2">
                              {driver.firstName} {driver.lastName}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" color="text.secondary">
                              {driver.assignedDeliveries.length} deliveries
                            </Typography>
                          }
                        />
                        {driver.assignedDeliveries.length > 0 && (
                          <IconButton size="small">
                            {expandedDrivers.includes(driver.id) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </IconButton>
                        )}
                      </ListItem>

                      <Collapse in={expandedDrivers.includes(driver.id)}>
                        <Divider />
                        <List dense sx={{ bgcolor: mode === 'dark' ? alpha('#000', 0.2) : alpha('#000', 0.02) }}>
                          {driver.assignedDeliveries.map((delivery) => (
                            <ListItem
                              key={delivery.id}
                              sx={{ py: 0.5 }}
                              secondaryAction={
                                <Chip
                                  size="small"
                                  label={delivery.status.replace('_', ' ')}
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    bgcolor: alpha(getStatusColor(delivery.status), 0.1),
                                    color: getStatusColor(delivery.status),
                                  }}
                                />
                              }
                            >
                              <ListItemText
                                primary={
                                  <Typography variant="caption" noWrap>
                                    {delivery.customerName}
                                  </Typography>
                                }
                                secondary={
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    noWrap
                                    sx={{ fontSize: '0.65rem' }}
                                  >
                                    {delivery.deliveryAddress?.city}
                                  </Typography>
                                }
                              />
                            </ListItem>
                          ))}
                        </List>
                      </Collapse>
                    </Paper>
                  ))
                )}
              </List>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Driver Assignment Modal */}
      <DriverAssignmentModal
        open={assignModalOpen}
        onClose={() => {
          setAssignModalOpen(false);
          setSelectedDeliveryForAssign(null);
        }}
        delivery={selectedDeliveryForAssign}
        onAssigned={() => {
          setSelectedDeliveries([]);
          setOptimizedRoute(null);
        }}
      />

      {/* Snackbar for feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

// Legacy export for backward compatibility
export const DeliverySchedule = DeliveryScheduleTab;

export default DeliveryScheduleTab;
